// src/components/practices/MoneyMagnet.js
// The Money Magnet — choose a bill or expense and find gratitude for what it actually gave you
// (the product, service, home, security, experience), rather than dwelling on the money leaving.
// Deliberately simple per spec ("establish its entry card and basic flow without
// overengineering it") — a single screen (name the expense + the gratitude response), closer in
// weight to Magic Rock than the multi-step flows (Magnificent Outcomes, Way Out of Negativity).
// Keep this distinct from Magic Money, a separate future Practice about gratitude for money
// already received more broadly, not the expense-reframe concept this one is built around.
import React, { useEffect, useRef, useState } from 'react'
import { supabase } from '../../supabaseClient'
import PageHeader from '../ui/PageHeader'
import PrimaryButton from '../ui/PrimaryButton'
import PracticeThankYouTap from './shared/PracticeThankYouTap'
import PracticeComplete from './shared/PracticeComplete'
import './shared/PracticeHeading.css'
import './MoneyMagnet.css'

const REREAD_MS = 2000

export default function MoneyMagnet({ practice, session, city, onComplete, onBack, augustTheme, onAugustThemeChange }) {
  // Starts on 'practice' directly — the detail screen (AugustPracticeDetail) already shows this
  // Practice's tagline + description before "Begin Practice" is tapped; a separate in-flow intro
  // phase repeating that same copy was an extra tap with no new information, not a real step
  // (never counted toward any step total).
  const [phase, setPhase] = useState('practice') // 'practice' | 'complete'
  const [expense, setExpense] = useState('')
  const [gratitude, setGratitude] = useState('')
  const [thanks, setThanks] = useState(0)
  const [reread, setReread] = useState(false)
  const [saving, setSaving] = useState(false)
  const rereadTimer = useRef(null)

  useEffect(() => () => clearTimeout(rereadTimer.current), [])

  const gratitudeTyped = gratitude.trim().length > 2
  const isReady = gratitudeTyped && thanks >= 3
  const eyebrow = `Day ${practice.unlockDay} · ${practice.name}`

  const tapThanks = () => {
    if (thanks >= 3) return
    const next = thanks + 1
    setThanks(next)
    if (next < 3) {
      setReread(true)
      clearTimeout(rereadTimer.current)
      rereadTimer.current = setTimeout(() => setReread(false), REREAD_MS)
    }
  }

  const handleFinish = async () => {
    if (!isReady || saving) return
    setSaving(true)
    if (session) {
      await supabase.from('practice_completions').insert({
        user_id: session.user.id,
        city: city || 'august',
        practice_id: practice.id,
        payload: { expense, gratitude },
      })
    }
    setSaving(false)
    setPhase('complete')
  }

  if (phase === 'practice') {
    return (
      <div className="practice-screen" data-city="august">
        <PageHeader
          eyebrow={eyebrow} title="What Did This Give You?"
          showBack onBack={onBack}
          augustTheme={augustTheme} onAugustThemeChange={onAugustThemeChange}
        />
        <div className="practice-screen-body">
          <p className="practice-question">An expense or bill</p>
          <div className="stops-input-row">
            <input
              type="text"
              className="magnet-expense-input"
              placeholder="Rent, electricity, groceries…"
              value={expense}
              onChange={e => setExpense(e.target.value)}
            />
          </div>

          <p className="practice-question">What did this give you?</p>
          <div className="stops-input-row">
            <textarea
              className="stops-input"
              placeholder="I'm grateful for…"
              value={gratitude}
              onChange={e => setGratitude(e.target.value)}
            />
          </div>

          <PracticeThankYouTap
            count={thanks}
            reread={reread}
            canTap={gratitudeTyped}
            onTap={tapThanks}
            writtenText={gratitude}
          />

          <PrimaryButton onClick={isReady ? handleFinish : undefined} disabled={!isReady || saving}>
            <span>Done</span>
            <span>✦</span>
          </PrimaryButton>
        </div>
      </div>
    )
  }

  // phase === 'complete'
  return (
    <PracticeComplete
      headline="Practice Complete"
      subline="That bill gave you more than it took."
      onDone={onComplete}
    />
  )
}
